import {
  useCallback,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import { Firestore } from 'firebase/firestore';

import { MeetingType, Day, Compare, FiltersState } from '@/types';

interface CompassContext {
  filters: FiltersState;
  firestore: null | Firestore;
  map: null | google.maps.Map;
  mapCenter: null | google.maps.LatLngLiteral;
  meetingFocus: null | string;
  placeMarker: null | google.maps.marker.AdvancedMarkerElement;
  showFetchButton: boolean;
  userMarker: null | google.maps.marker.AdvancedMarkerElement;
}

export interface CompassContextType {
  acceptedMapCenterRef: React.MutableRefObject<google.maps.LatLngLiteral | null>;
  state: CompassContext;
  dispatch: React.Dispatch<Actions>;
}

type Actions =
  | FirestoreAction
  | MapAction
  | MapCenterAction
  | AcceptMapCenterAction
  | MeetingFocusAction
  | DaysAction
  | TimeAction
  | TypesAction
  | ClearFiltersAction
  | ShowFetchButtonAction
  | UserMarkerAction
  | PlaceMarkerAction;

export type FirestoreAction = {
  type: 'SET_FIRESTORE';
  payload: Firestore;
};

type MapAction = {
  type: 'SET_MAP';
  payload: google.maps.Map;
};

type MapCenterAction = {
  type: 'SET_MAP_CENTER';
  payload: google.maps.LatLngLiteral;
};

type AcceptMapCenterAction = {
  type: 'ACCEPT_MAP_CENTER';
  payload: google.maps.LatLngLiteral;
};

export type MeetingFocusAction = {
  type: 'SET_MEETING_FOCUS';
  payload: string;
};

type DaysAction = {
  type: 'SET_DAYS';
  payload: Day[];
};

type TimeAction = {
  type: 'SET_TIME';
  payload: {
    compare: Compare;
    value: string;
  };
};

type TypesAction = {
  type: 'SET_TYPES';
  payload: MeetingType[];
};

type ClearFiltersAction = {
  type: 'CLEAR_FILTERS';
};

type ShowFetchButtonAction = {
  type: 'SHOW_FETCH_BUTTON';
  payload: boolean;
};

type UserMarkerAction = {
  type: 'SET_USER_MARKER';
  payload: google.maps.marker.AdvancedMarkerElement | null;
};

type PlaceMarkerAction = {
  type: 'SET_PLACE_MARKER';
  payload: google.maps.marker.AdvancedMarkerElement | null;
};

export const init: CompassContext = {
  filters: {
    day: [],
    time: {
      compare: null,
      value: '0000',
    },
    type: [],
  },
  firestore: null,
  map: null,
  mapCenter: null,
  meetingFocus: null,
  placeMarker: null,
  showFetchButton: false,
  userMarker: null,
};

export const reducer = (
  state: CompassContext,
  action: Actions
): CompassContext => {
  switch (action.type) {
    case 'SET_FIRESTORE':
      return {
        ...state,
        firestore: action.payload as FirestoreAction['payload'],
      };
    case 'SET_MAP':
      return {
        ...state,
        map: action.payload as MapAction['payload'],
      };
    case 'SET_MAP_CENTER':
      return {
        ...state,
        mapCenter: action.payload as MapCenterAction['payload'],
      };
    case 'ACCEPT_MAP_CENTER':
      return {
        ...state,
        mapCenter: action.payload as AcceptMapCenterAction['payload'],
        showFetchButton: false,
      };
    case 'SET_MEETING_FOCUS':
      return {
        ...state,
        meetingFocus: action.payload as MeetingFocusAction['payload'],
      };
    case 'SET_DAYS':
      return {
        ...state,
        filters: {
          ...state.filters,
          day: action.payload as DaysAction['payload'],
        },
      };
    case 'SET_TIME':
      return {
        ...state,
        filters: {
          ...state.filters,
          time: action.payload as TimeAction['payload'],
        },
      };
    case 'SET_TYPES':
      return {
        ...state,
        filters: {
          ...state.filters,
          type: action.payload as MeetingType[],
        },
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {
          day: [],
          time: {
            compare: null,
            value: '0000',
          },
          type: [],
        },
      };
    case 'SHOW_FETCH_BUTTON':
      return {
        ...state,
        showFetchButton: action.payload as boolean,
      };
    case 'SET_USER_MARKER':
      return {
        ...state,
        userMarker: action.payload as UserMarkerAction['payload'],
      };
    case 'SET_PLACE_MARKER':
      return {
        ...state,
        placeMarker: action.payload as PlaceMarkerAction['payload'],
      };
    default:
      return state;
  }
};

export const CompassContext = createContext<CompassContextType | undefined>(
  undefined
);

export const CompassContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, reducerDispatch] = useReducer(reducer, init);
  const acceptedMapCenterRef = useRef<google.maps.LatLngLiteral | null>(
    init.mapCenter
  );
  const dispatch = useCallback<React.Dispatch<Actions>>((action) => {
    if (action.type === 'ACCEPT_MAP_CENTER') {
      acceptedMapCenterRef.current = action.payload;
    }
    reducerDispatch(action);
  }, []);
  const value = { acceptedMapCenterRef, state, dispatch };
  return (
    <CompassContext.Provider value={value}>{children}</CompassContext.Provider>
  );
};

export const useCompassContext = () => {
  const ctx = useContext(CompassContext);
  return ctx;
};
